/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Picking someone's roles: one base role, plus any number of add-ons.
 *
 * Shared by the create and edit paths in User Management so the ordering rule
 * lives in one place. Add-ons are additive overlays on top of a base grant --
 * offering them first invites an admin to build a user out of add-ons alone,
 * who then has no base role and, once the tenant enforces, sees nothing at all.
 *
 * Falls back to nothing when the tenant has no roles: GET /users/assignable-roles
 * answers [] on a tenant that has not run migration 030, and an empty picker
 * reads as a broken control rather than an absent feature. The caller renders
 * the legacy dropdown in that case.
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormHelperText,
} from '@mui/material';
import MDBox from 'components/MDBox';

export const splitRoles = (roles) => ({
  baseRoles: (roles || []).filter((r) => r.role_kind === 'base'),
  addonRoles: (roles || []).filter((r) => r.role_kind === 'addon'),
});

function RolePicker({
  roles,
  baseRoleId,
  addonRoleIds,
  onBaseRoleChange,
  onAddonRolesChange,
  disabled,
  required,
}) {
  const { baseRoles, addonRoles } = useMemo(() => splitRoles(roles), [roles]);

  const selectedBase = baseRoles.find((r) => r.id === baseRoleId) || null;
  const selectedAddons = addonRoles.filter((r) => (addonRoleIds || []).includes(r.id));

  // Helper text carries the consequence rather than the definition: an admin
  // choosing a role needs to know what it does to the person, not what the
  // word means.
  const baseHelp = selectedBase
    ? (selectedBase.is_super
      ? 'Full access to every record and every setting in this account.'
      : selectedBase.description || "This person's main role.")
    : 'Everyone has exactly one base role.';

  return (
    <>
      <FormControl fullWidth margin="normal" required={required}>
        <InputLabel>Role</InputLabel>
        <Select
          value={selectedBase ? selectedBase.id : ''}
          label="Role"
          onChange={(e) => onBaseRoleChange(e.target.value === '' ? null : e.target.value)}
          disabled={disabled}
        >
          {baseRoles.map((role) => (
            <MenuItem key={role.id} value={role.id}>
              {role.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText error={Boolean(selectedBase && selectedBase.is_super)}>
          {baseHelp}
        </FormHelperText>
      </FormControl>

      {addonRoles.length > 0 && (
        <MDBox mt={1}>
          <Autocomplete
            multiple
            fullWidth
            options={addonRoles}
            // Add-ons only widen access, so they are meaningless without a base
            // role underneath them to widen.
            disabled={disabled || !baseRoleId}
            value={selectedAddons}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, values) => onAddonRolesChange(values.map((v) => v.id))}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Additional roles"
                helperText={
                  baseRoleId
                    ? 'Optional. Add-on roles only widen access — they never take it away.'
                    : 'Choose a base role first.'
                }
              />
            )}
          />
        </MDBox>
      )}
    </>
  );
}

RolePicker.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.object),
  baseRoleId: PropTypes.number,
  addonRoleIds: PropTypes.arrayOf(PropTypes.number),
  onBaseRoleChange: PropTypes.func.isRequired,
  onAddonRolesChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

RolePicker.defaultProps = {
  roles: [],
  baseRoleId: null,
  addonRoleIds: [],
  disabled: false,
  required: false,
};

export default RolePicker;
